import { Col, Form, Input, InputNumber, Modal, Radio, Row, Select } from "antd";
import React, { useEffect, useState } from "react";

import SingleImageUpload from "../../../components/SingleImageUpload";
import lang from "../../../helper/langHelper";
import { Severty, ShowToast } from "../../../helper/toast";
import useRequest from "../../../hooks/useRequest";

const FileType = ["image/png", "image/jpg", "image/jpeg", "image/avif", "image/webp", "image/gif"];

const normalizeOptions = (items = []) =>
  (Array.isArray(items) ? items : [])
    .map((item) => ({
      value: item?._id || item?.id || item?.value || item,
      label: item?.label || item?.name || item?.title || item?.slug || item?.value || item?._id || item,
      raw: item?.raw || item,
    }))
    .filter((item) => item.value);

const AddForm = ({ api, show, hide, data, refresh, productOptions = [] }) => {
  const [form] = Form.useForm();
  const { request } = useRequest();
  const [loading, setLoading] = useState(false);
  const [thumbnail, setThumbnail] = useState(null);
  const [isThumbnailRemove, setIsThumbnailRemove] = useState(false);

  useEffect(() => {
    if (!data) {
      form.resetFields();
      setThumbnail(null);
      setIsThumbnailRemove(false);
      return;
    }

    form.setFieldsValue({
      ...data,
      product_id: data?.product_id?._id || data?.product_id || null,
      is_active: data?.is_active ?? true,
      price: data?.price ?? null,
      stock: data?.stock ?? null,
      stock_keeping_unit: data?.stock_keeping_unit || "",
    });
    setThumbnail(data?.thumbnail || null);
    setIsThumbnailRemove(false);
  }, [data, form]);

  const handleThumbnailChange = (file) => {
    if (!file) {
      setThumbnail(null);
      setIsThumbnailRemove(Boolean(data));
      return;
    }

    setIsThumbnailRemove(false);
    setThumbnail(file);
  };

  const onCreate = (values) => {
    setLoading(true);
    const payload = new FormData();

    payload.append("title", values.title || "");
    payload.append("description", values.description || "");
    payload.append("product_id", values.product_id || "");
    payload.append("price", values.price ?? "");
    payload.append("stock", values.stock ?? "");
    payload.append("stock_keeping_unit", values.stock_keeping_unit || "");
    payload.append("is_active", values.is_active ?? true);

    const selectedFile = thumbnail instanceof File ? thumbnail : thumbnail?.originFileObj;
    if (selectedFile instanceof File) {
      payload.append("image", selectedFile);
    }

    if (data && isThumbnailRemove) {
      payload.append("isThumbnailRemove", "true");
    }

    request({
      url: `${data ? `${api.addEdit}/${data._id}` : api.addEdit}`,
      method: data ? "PUT" : "POST",
      data: payload,
      header: {
        "Content-Type": "multipart/form-data",
      },
      onSuccess: (response) => {
        setLoading(false);
        if (response.status) {
          ShowToast(response.message, Severty.SUCCESS);
          hide();
          refresh();
        } else {
          ShowToast(response.message, Severty.ERROR);
        }
      },
      onError: (error) => {
        setLoading(false);
        ShowToast(error?.response?.data?.message || error?.message || "Something went wrong", Severty.ERROR);
      },
    });
  };

  return (
    <Modal
      open={show}
      width={900}
      okText={data ? lang("Update") : lang("Add")}
      cancelText={lang("Cancel")}
      onCancel={hide}
      okButtonProps={{
        form: "variant-form",
        htmlType: "submit",
        loading,
      }}
      centered
      className="tab_modal"
    >
      <Form
        id="variant-form"
        form={form}
        onFinish={onCreate}
        layout="vertical"
        initialValues={{
          is_active: true,
          price: null,
          stock: null,
        }}
      >
        <h4 className="modal_title_cls">{data ? lang("Edit Variant") : lang("Add New Variant")}</h4>
        <Row gutter={[16, 0]}>
          <Col span={24} md={12}>
            <Form.Item
              label={lang("Title")}
              name="title"
              rules={[
                { required: true, message: lang("Title is required") },
                { min: 2, message: lang("Title should contain at least 2 characters!") },
                { max: 200, message: lang("Title should not contain more then 200 characters!") },
              ]}
              normalize={(value) => value?.trimStart?.() ?? value}
            >
              <Input autoComplete="off" placeholder={lang("Enter Title")} />
            </Form.Item>
          </Col>

          <Col span={24} md={12}>
            <Form.Item label={lang("Product")} name="product_id" rules={[{ required: true, message: lang("Please select product!") }]}>
              <Select allowClear showSearch placeholder={lang("Select Product")} optionFilterProp="label" getPopupContainer={(triggerNode) => triggerNode.parentNode}>
                {normalizeOptions(productOptions).map((item) => (
                  <Select.Option key={item.value} value={item.value} label={item.label}>
                    {item.label}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          <Col span={24} md={12}>
            <Form.Item label={lang("SKU")} name="stock_keeping_unit">
              <Input autoComplete="off" placeholder={lang("Enter SKU")} />
            </Form.Item>
          </Col>

          <Col span={24} md={12}>
            <Form.Item label={lang("Price")} name="price">
              <InputNumber min={0} style={{ width: "100%" }} placeholder={lang("Enter Price")} />
            </Form.Item>
          </Col>

          <Col span={24} md={12}>
            <Form.Item label={lang("Stock")} name="stock">
              <InputNumber min={0} style={{ width: "100%" }} placeholder={lang("Enter Stock")} />
            </Form.Item>
          </Col>

          <Col span={24} md={12}>
            <Form.Item label={lang("Status")} name="is_active">
              <Radio.Group>
                <Radio value={true}>{lang("Active")}</Radio>
                <Radio value={false}>{lang("De-Active")}</Radio>
              </Radio.Group>
            </Form.Item>
          </Col>

          <Col span={24} md={24}>
            <Form.Item label={lang("Description")} name="description">
              <Input.TextArea rows={4} placeholder={lang("Enter Description")} />
            </Form.Item>
          </Col>

          <Col span={24} md={12}>
            <Form.Item label={lang("Thumbnail")} name="thumbnail">
              <SingleImageUpload value={thumbnail} fileType={FileType} btnName={"Thumbnail"} imageType="variant" onChange={handleThumbnailChange} size={5} />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default AddForm;
